import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material'
import { NavLink } from 'react-router-dom'
import { visuallyHidden } from '@mui/utils'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import SortableHeader from '../../rsmCoreComponents/components/SortableHeader/SortableHeader'
import { UserApplicationData } from '../../store/userManagement/userManagementSearchService'
import UserManagementActionMenu from './UserManagementActionMenu'
import OverflowTooltip from '../OverFlowToolTip'
import tokens from '../../styles/tokens.json'
import { Styles } from '../../types'
import { setSelectedUserProfileData } from '../../store/userManagement/userManagementProfileSlice'

/**
 * Styles for UserManagementTable component
 * Following InvoicesTable.tsx pattern for structural consistency
 * Implements responsive breakpoints for desktop/tablet/mobile viewports
 */
const styles: Styles = {
  userManagementTable: (theme) => ({
    // -------------------------------------------------------------------------
    // Base Table Styles (following InvoicesTable pattern)
    // -------------------------------------------------------------------------
    '&.MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
      '& th.MuiTableCell-root': {
        fontFamily: 'Prelo-Bold, sans-serif',
      },
      '& td.MuiTableCell-root': {
        fontFamily: 'Prelo-Book, sans-serif',
      },
      '& .MuiTableCell-head': {
        padding: '1rem',
        paddingRight: 'revert',
        fontSize: '0.875rem',
        lineHeight: '1rem',
        textTransform: 'uppercase',
        verticalAlign: 'top',
        alignItems: 'center',
      },
      '& tbody': {
        '& .MuiTableRow-root': {
          '&:nth-of-type(odd)': {
            backgroundColor: tokens.colors.rsmGray.accessibility,
          },
        },
      },
      '& .MuiTableCell-body': {
        padding: '1.5rem 1rem',
        fontSize: '1rem',
        lineHeight: '1.5rem',
        '&:nth-of-type(1), &:nth-of-type(2), &:nth-of-type(3), &:nth-of-type(4), &:nth-of-type(5), &:nth-of-type(6), &:nth-of-type(7)':
          {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
      },
      // actions column
      '& thead th.actions, & tbody td.actions': {
        width: '3.5rem',
        minWidth: '3.5rem',
        maxWidth: '3.5rem',
        textAlign: 'right',
        paddingRight: '0.5rem',
        paddingLeft: '0.5rem',
        alignItems: 'right',
      },
    },
    '.sortable-header': {
      cursor: 'pointer',
    },
    // -------------------------------------------------------------------------
    // Desktop Column Widths (7 data columns with actions fixed)
    // -------------------------------------------------------------------------
    [theme.breakpoints.only('desktop')]: {
      '& .MuiTableCell-root': {
        '&:nth-of-type(1)': { width: '18%' }, // Name
        '&:nth-of-type(2)': { width: '26%' }, // Email
        '&:nth-of-type(3)': { width: '16%' }, // Tool
        '&:nth-of-type(4)': { width: '12%' }, // Role
        '&:nth-of-type(5)': { width: '11%' }, // Status
        '&:nth-of-type(6)': { width: '13%' }, // Last Login
        // Actions has fixed width via .actions class
      },
    },
    // -------------------------------------------------------------------------
    // Tablet Responsive: Hide less critical columns (Role)
    // -------------------------------------------------------------------------
    [theme.breakpoints.only('tablet')]: {
      '& .MuiTableCell-root': {
        '&:nth-of-type(4)': {
          display: 'none', // Role
        },
      },
    },
    // -------------------------------------------------------------------------
    // Mobile Responsive: Show only essential columns (Name, Email, Status, Actions)
    // -------------------------------------------------------------------------
    [theme.breakpoints.down('mobile')]: {
      '& .MuiTableCell-root': {
        // Hide Tool (3), Role (4), Last Login (6)
        '&:nth-of-type(3), &:nth-of-type(4), &:nth-of-type(6)': {
          display: 'none',
        },
      },
    },
  }),
}

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'underline',
  padding: '0.125rem 0.3125rem 0.125rem 0.3125rem',
}))

/**
 * Props interface for UserManagementTable component
 * Matches InvoicesTable pattern with additional pagination props
 */
interface UserManagementTableProps {
  usersData: UserApplicationData[]
  onSort: (columnName: string, direction: 'asc' | 'desc') => void
  sortColumn: string
  sortDirection: 'asc' | 'desc'
  pageNumber: number
  pageSize: number
}

/**
 * UserManagementTable Component
 *
 * Primary interactive table component displaying user data in sortable 8-column format
 * with pagination and action menus. Implements comprehensive Material-UI Table with
 * SortableHeader for all data columns and UserManagementActionMenu for per-row actions.
 *
 * Features:
 * - Three-state column sorting (ascending → descending → default)
 * - Client-side pagination via useMemo array slicing
 * - Responsive column visibility for desktop/tablet/mobile breakpoints
 * - OverflowTooltip for truncated text in Name, Email, Address columns
 * - Zebra striping for alternating rows
 * - Date formatting for Last Login with N/A handling for DateTime.MinValue
 * - WCAG 2.1 AA accessibility with visuallyHidden caption and ARIA labels
 * - Full internationalization support via useTranslation hook
 *
 * Architecture Pattern:
 * Follows InvoicesTable.tsx architectural pattern with styled components,
 * comprehensive breakpoint styling, and performance-optimized memoization.
 *
 * @param usersData - Array of UserApplicationData objects to display (filtered/sorted dataset)
 * @param onSort - Callback function to update Redux sort state in parent component
 * @param sortColumn - Current sort column name for SortableHeader visual indicators
 * @param sortDirection - Current sort direction ('asc' or 'desc')
 * @param pageNumber - Current page number (1-indexed) for pagination window
 * @param pageSize - Number of users per page (default: 10)
 */
const UserManagementTable = ({
  usersData,
  onSort,
  sortColumn,
  sortDirection,
  pageNumber,
  pageSize,
}: UserManagementTableProps) => {
  const { t } = useTranslation()
  const [isMounted, setIsMounted] = useState(false)
  const dispatch = useDispatch()

  /**
   * Set isMounted state to true after component mounts
   * Required for SortableHeader focus restoration logic
   */
  useEffect(() => {
    setIsMounted(true)
  }, [])

  /**
   * Handle sort callback - toggle between asc/desc directions
   * Third click reverts to default sort (handled by parent component)
   *
   * @param columnName - Name of column to sort by
   */
  const handleSort = useCallback(
    (columnName: string) => {
      const newSortDirection: 'asc' | 'desc' =
        sortColumn === columnName && sortDirection === 'asc' ? 'desc' : 'asc'
      onSort(columnName, newSortDirection)
    },
    [sortColumn, sortDirection, onSort],
  )

  /**
   * Calculate paginated users for current page
   * Slices usersData array based on pageNumber and pageSize
   * Memoized for performance optimization
   */
  const paginatedUsers = useMemo(() => {
    // Convert pageSize to a number (in case it's passed as a string)
    const numericPageSize = Number(pageSize)
    const startIndex = (pageNumber - 1) * numericPageSize
    const endIndex = startIndex + numericPageSize
    return usersData.slice(startIndex, endIndex)
  }, [usersData, pageNumber, pageSize])

  /**
   * Helper function to generate sortable table headers
   * Wraps SortableHeader component with TableCell for consistent styling
   *
   * @param columnName - Column identifier for sorting logic
   * @param displayKey - Translation key for column header label
   * @returns TableCell with SortableHeader component
   */
  const getSortableHeader = useCallback(
    (columnName: string, displayValue: string) => (
      <TableCell>
        <SortableHeader
          columnName={columnName}
          currentSortColumn={sortColumn || ''}
          sortDirection={sortDirection || ''}
          isMounted={isMounted}
          handleSort={handleSort}>
          {displayValue}
        </SortableHeader>
      </TableCell>
    ),
    [sortColumn, sortDirection, isMounted, handleSort],
  )

  /**
   * Format Last Login as MM/DD/YYYY (US format) or "N/A" for missing/invalid values.
   */
  const formatLastLogin = useCallback(
    (lastLogin: Date) => {
      const d = lastLogin ? new Date(lastLogin) : null
      if (
        !d ||
        Number.isNaN(d.getTime()) ||
        d.getTime() === 0 ||
        d.getFullYear() === 1
      ) {
        return t('UserManagement.Table.NoLastLogin', { defaultValue: 'N/A' })
      }
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${mm}/${dd}/${yyyy}`
    },
    [t],
  )

  const getUserNameLink = useCallback(
    (name: string, email: string, index: number) => (
      <OverflowTooltip id={`name-tooltip-${index}`}>
        <StyledNavLink
          // eslint-disable-next-line jsx-a11y/tabindex-no-positive
          tabIndex={0}
          to="/user-management/userprofile"
          onClick={() => {
            dispatch(
              setSelectedUserProfileData({ userEmail: email, userName: name }),
            )
          }}
          data-testid={`Lnk_User_Name_${index}`}>
          {name}
        </StyledNavLink>
      </OverflowTooltip>
    ),
    [],
  )

  return (
    <TableContainer component={Paper}>
      <Table
        sx={styles.userManagementTable}
        aria-label={t('UserManagement.AccessibilityLabels.UserTable', {
          defaultValue: 'User management table',
        })}>
        {/* Visually hidden caption for screen readers (WCAG 2.1 AA compliance) */}
        <Box component="caption" sx={visuallyHidden}>
          {t('UserManagement.Results.ResultsCount', {
            defaultValue: '{{count}} users found',
            count: usersData.length,
          })}
        </Box>
        <TableHead>
          <TableRow>
            {/* Column 1: Name (sortable) */}
            {getSortableHeader('name', t('UserManagement.TableHeaders.Name'))}
            {/* Column 2: Email Address (sortable) */}
            {getSortableHeader('email', t('UserManagement.TableHeaders.Email'))}
            {/* Column 3: Tool (sortable) */}
            {getSortableHeader('tool', t('UserManagement.TableHeaders.Tool'))}
            {/* Column 4: Role (sortable) */}
            {getSortableHeader('role', t('UserManagement.TableHeaders.Role'))}
            {/* Column 5: Status (sortable) */}
            {getSortableHeader(
              'status',
              t('UserManagement.TableHeaders.Status'),
            )}
            {/* Column 6: Last Login (sortable) */}
            {getSortableHeader(
              'lastLogin',
              t('UserManagement.TableHeaders.LastLogin'),
            )}
            {/* Column 7: Actions (not sortable) */}
            <TableCell className="actions">
              <Box component="span" sx={visuallyHidden}>
                {t('Common.Actions', 'Actions')}
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedUsers.map((user, index) => (
            <TableRow
              key={`user-row-${user.email}`}
              data-testid={`UserManagement_TableRow_${index}`}>
              {/* Column 1: Name with OverflowTooltip */}
              <TableCell data-testid={`UserManagement_Name_${index}`}>
                <OverflowTooltip id={`name-tooltip-${index}`}>
                  {getUserNameLink(user.name || '', user.email || '', index)}
                </OverflowTooltip>
              </TableCell>
              {/* Column 2: Email with OverflowTooltip */}
              <TableCell data-testid={`UserManagement_Email_${index}`}>
                <OverflowTooltip id={`email-tooltip-${index}`}>
                  {user.email || ''}
                </OverflowTooltip>
              </TableCell>
              {/* Column 3: Tool */}
              <TableCell data-testid={`UserManagement_Tool_${index}`}>
                <OverflowTooltip id={`tool-tooltip-${index}`}>
                  {user.tool || ''}
                </OverflowTooltip>
              </TableCell>
              {/* Column 4: Role */}
              <TableCell data-testid={`UserManagement_Role_${index}`}>
                <OverflowTooltip id={`role-tooltip-${index}`}>
                  {user.role || ''}
                </OverflowTooltip>
              </TableCell>
              {/* Column 5: Status */}
              <TableCell data-testid={`UserManagement_Status_${index}`}>
                <OverflowTooltip id={`status-tooltip-${index}`}>
                  {user.status || ''}
                </OverflowTooltip>
              </TableCell>
              {/* Column 6: Last Login with formatted date */}
              <TableCell data-testid={`UserManagement_LastLogin_${index}`}>
                <OverflowTooltip id={`lastlogin-tooltip-${index}`}>
                  {formatLastLogin(user.lastLogin as unknown as Date)}
                </OverflowTooltip>
              </TableCell>
              {/* Column 7: Action Menu */}
              <TableCell
                className="actions"
                data-testid={`UserManagement_Actions_${index}`}>
                <UserManagementActionMenu
                  user={user}
                  dataTestId={`UserManagement_ActionMenu_${index}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default UserManagementTable
